/*
  ЦИФРОВОЙ ДИАК ЗФ — KANBAN BOARD
  Канбан-доска карточки проекта. Колонки настраиваются, карточки = BacklogItem.
  DnD через @dnd-kit: колонки-горизонтально, карточки — по вертикали внутри
  и между колонками, с DragOverlay и оптимистичным перестроением при drag-over.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowRightLeft,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  GripVertical,
  ListChecks,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Paperclip,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  type BacklogItem,
  type BacklogItemPriority,
  type KanbanColumn,
} from "@/store/projectStore";
import { useProjects } from "@/contexts/ProjectContext";
import { useNotifications } from "@/contexts/NotificationsContext";
import {
  colorForAssignee,
  initialsFor,
  normalizeAssigneeKey,
  preferredAssigneeLabel,
} from "@/lib/assigneeDisplay";
import { SingleAssigneePicker } from "./SingleAssigneePicker";
import {
  DUE_TONE_STYLES,
  describeDue,
  formatShortRuDate,
  isDoneColumnId,
  toIsoDay,
} from "@/lib/kanbanDate";
import { ALL_BACKLOG_TAGS, BACKLOG_TAGS } from "@/lib/backlogTags";
import type {
  BacklogAttachment,
  BacklogComment,
  BacklogItemTag,
  BacklogSubtask,
} from "@/store/projectStore";
import { BacklogAttachments } from "./BacklogAttachments";
import { BacklogComments } from "./BacklogComments";
import { BacklogSubtasks } from "./BacklogSubtasks";
import { RichTextEditor } from "./RichText";
import { apiFetchFactTasks } from "@/lib/api";
import { InlineText, InlineSelect } from "./InlineEdit";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const COLUMN_WIDTH_PX = 244;

const PRIORITY_META: Record<
  BacklogItemPriority,
  { color: string; label: string; tint: string }
> = {
  critical: { color: "#D0021B", tint: "#D0021B22", label: "Критический" },
  high: { color: "#E85D04", tint: "#E85D0422", label: "Высокий" },
  medium: { color: "#F5A623", tint: "#F5A62322", label: "Средний" },
  low: { color: "#00A651", tint: "#00A65122", label: "Низкий" },
};

const PRIORITY_OPTIONS = [
  { value: "critical", label: "Критический" },
  { value: "high", label: "Высокий" },
  { value: "medium", label: "Средний" },
  { value: "low", label: "Низкий" },
] as const;

/* ────────────────────────────── Props ───────────────────────────────── */

interface KanbanBoardProps {
  projectId: string;
  columns: KanbanColumn[];
  items: BacklogItem[];
  canEdit: boolean;
}

/* ────────────────────────── Main component ──────────────────────────── */

export function KanbanBoard({ projectId, columns, items, canEdit }: KanbanBoardProps) {
  const {
    projects,
    addBacklogItem,
    updateBacklogItem,
    removeBacklogItem,
    moveBacklogItemToColumn,
    moveBacklogItemToProject,
    addKanbanColumn,
    renameKanbanColumn,
    removeKanbanColumn,
    reorderKanbanColumns,
  } = useProjects();

  // ВАЖНО про порядок карточек:
  //
  // `items` (= `project.backlog`) хранятся в контексте без пересортировки —
  // там, как карточки исторически легли в массив. Каждая карточка имеет
  // `kanbanOrder` (0..N-1 ВНУТРИ своей колонки), но ПОРЯДОК В МАССИВЕ может
  // не совпадать с `kanbanOrder` после локальных мутаций (см.
  // `moveBacklogItemToColumn` в ProjectContext).
  //
  // Поэтому при синхронизации с props мы один раз сортируем items так:
  //   1) сначала группируем по колонкам в порядке `columns.order`,
  //   2) внутри каждой колонки — по `kanbanOrder`.
  // Дальше `localItems` всегда находится в этом «правильном» порядке, и
  // `arrayMove` / cross-column-insert внутри drag сохраняют этот инвариант.
  // `itemsByColumn` ниже просто фильтрует по статусу, не пересортировывая —
  // это критично, чтобы drag-preview не откатывался во время drag.
  const sortItemsByColumn = useCallback(
    (raw: BacklogItem[]): BacklogItem[] => {
      const orderByCol = new Map<string, number>();
      [...columns]
        .sort((a, b) => a.order - b.order)
        .forEach((c, i) => orderByCol.set(c.id, i));
      return [...raw].sort((a, b) => {
        const ao = orderByCol.get(a.status) ?? Number.MAX_SAFE_INTEGER;
        const bo = orderByCol.get(b.status) ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        const ak = Number.isFinite(a.kanbanOrder) ? a.kanbanOrder : Number.POSITIVE_INFINITY;
        const bk = Number.isFinite(b.kanbanOrder) ? b.kanbanOrder : Number.POSITIVE_INFINITY;
        if (ak !== bk) return ak - bk;
        return a.createdAt.localeCompare(b.createdAt);
      });
    },
    [columns],
  );

  const [localItems, setLocalItems] = useState<BacklogItem[]>(() =>
    sortItemsByColumn(items),
  );
  const itemsRef = useRef<BacklogItem[]>(items);
  // Кэш отсортированной версии `items` — используется для быстрого «откатить
  // preview к серверному порядку» при cancel/невалидном drop.
  const sortedItemsRef = useRef<BacklogItem[]>(localItems);
  // Синхронный ref на текущий `localItems`. dnd-kit может вызвать onDragEnd
  // до того, как React закоммитит последний setLocalItems из onDragOver, и
  // тогда замыкание handleDragEnd видит устаревший state. Через ref читаем
  // самое свежее значение.
  const localItemsRef = useRef<BacklogItem[]>(localItems);
  // Активный drag — ref, чтобы во время drag не перезатирать preview, если
  // родитель просто перерендерит нас с тем же самым `items`.
  const activeIdRef = useRef<string | null>(null);
  useEffect(() => {
    itemsRef.current = items;
    const sorted = sortItemsByColumn(items);
    sortedItemsRef.current = sorted;
    if (activeIdRef.current === null) {
      setLocalItems(sorted);
      localItemsRef.current = sorted;
    }
  }, [items, sortItemsByColumn]);

  // Хелпер: setLocalItems + синхронное обновление ref.
  const updateLocalItems = useCallback(
    (updater: (prev: BacklogItem[]) => BacklogItem[]) => {
      setLocalItems((prev) => {
        const next = updater(prev);
        localItemsRef.current = next;
        return next;
      });
    },
    [],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<"card" | "column" | null>(null);
  // Расширенная карточка (инлайн-редактор).
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // Deep-link: ?card=<id> (из уведомлений) — раскрыть карточку и проскроллить к ней.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const target = params.get("card");
    if (!target) return;
    if (!items.some((it) => it.id === target)) return;
    setExpandedCardId(target);
    // Скролл к карточке через небольшой тайм-аут, чтобы дать DOM отрендериться.
    window.setTimeout(() => {
      const el = document.querySelector(`[data-kanban-card-id="${target}"]`) as HTMLElement | null;
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    params.delete("card");
    const next = `${window.location.pathname}${
      params.toString() ? `?${params.toString()}` : ""
    }${window.location.hash}`;
    window.history.replaceState(null, "", next);
  }, [items, projectId]);

  const sortedColumns = useMemo(
    () => [...columns].sort((a, b) => a.order - b.order),
    [columns],
  );
  const columnIds = useMemo(() => sortedColumns.map((c) => c.id), [sortedColumns]);

  const itemsByColumn = useMemo(() => {
    const map = new Map<string, BacklogItem[]>();
    for (const col of sortedColumns) map.set(col.id, []);
    const fallback = sortedColumns[0]?.id;
    // Сортировка НЕ требуется: `localItems` уже находятся в правильном
    // порядке (см. sortItemsByColumn в useEffect выше + arrayMove во время
    // drag, который сохраняет инвариант). Если бы тут была пересортировка по
    // kanbanOrder, drag-preview мгновенно откатывался бы — kanbanOrder во
    // время drag не обновляется.
    for (const it of localItems) {
      const colId = map.has(it.status) ? it.status : fallback;
      if (!colId) continue;
      map.get(colId)!.push(it);
    }
    return map;
  }, [localItems, sortedColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Удобные поиски при dnd-событиях.
  const findContainerOf = useCallback(
    (id: string | null): string | null => {
      if (!id) return null;
      if (columnIds.includes(id)) return id;
      // Внутренний droppable «список карточек колонки»: id = `${col.id}::cards`.
      if (id.endsWith("::cards")) {
        const colId = id.slice(0, -"::cards".length);
        if (columnIds.includes(colId)) return colId;
      }
      const it = localItemsRef.current.find((x) => x.id === id);
      return it ? it.status : null;
    },
    [columnIds],
  );

  const handleDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    const type = (e.active.data.current?.type as "card" | "column" | undefined) ?? null;
    activeIdRef.current = id;
    setActiveId(id);
    setActiveType(type);
    if (type === "card") setExpandedCardId(null);
  };

  const handleDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over) return;
    if ((active.data.current?.type ?? null) !== "card") return;
    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    if (activeIdStr === overIdStr) return;

    updateLocalItems((prev) => {
      const activeIdx = prev.findIndex((i) => i.id === activeIdStr);
      if (activeIdx < 0) return prev;
      const activeItem = prev[activeIdx]!;
      const overIdx = prev.findIndex((i) => i.id === overIdStr);

      // Внутри одной колонки — простой arrayMove. Поскольку itemsByColumn
      // сохраняет порядок из localItems, dnd-kit плавно анимирует карточки.
      if (overIdx >= 0 && prev[overIdx]!.status === activeItem.status) {
        if (activeIdx === overIdx) return prev;
        return arrayMove(prev, activeIdx, overIdx);
      }

      // Кросс-колоночный перенос. Колонка-цель: либо колонка over-карточки,
      // либо id самой колонки/её внутренней droppable-зоны.
      const overColumnFromData =
        (over.data.current?.columnId as string | undefined) ?? null;
      const overEndsCards = overIdStr.endsWith("::cards");
      const overCardsCol = overEndsCards
        ? overIdStr.slice(0, -"::cards".length)
        : null;
      const toCol =
        overIdx >= 0
          ? prev[overIdx]!.status
          : columnIds.includes(overIdStr)
            ? overIdStr
            : overCardsCol && columnIds.includes(overCardsCol)
              ? overCardsCol
              : overColumnFromData && columnIds.includes(overColumnFromData)
                ? overColumnFromData
                : null;
      if (!toCol) return prev;

      const withoutActive = prev.filter((i) => i.id !== activeIdStr);
      let insertIdx: number;
      if (overIdx >= 0) {
        const overInWithout = withoutActive.findIndex((i) => i.id === overIdStr);
        insertIdx = overInWithout >= 0 ? overInWithout : withoutActive.length;
      } else {
        // Над пустым/нижним droppable колонки — в конец её карточек.
        const cardsOfTo = withoutActive.filter((i) => i.status === toCol);
        const last = cardsOfTo[cardsOfTo.length - 1];
        insertIdx = last ? withoutActive.indexOf(last) + 1 : withoutActive.length;
      }
      const moved: BacklogItem = { ...activeItem, status: toCol };
      return [
        ...withoutActive.slice(0, insertIdx),
        moved,
        ...withoutActive.slice(insertIdx),
      ];
    });
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    const activeIdStr = String(active.id);
    const activeTypeVal = (active.data.current?.type as "card" | "column" | undefined) ?? null;
    setActiveId(null);
    setActiveType(null);
    activeIdRef.current = null;

    const resetLocal = () => {
      // ВАЖНО: возвращаемся именно к ОТСОРТИРОВАННОЙ версии — массив в
      // `itemsRef` (props) может быть в произвольном порядке после локальных
      // мутаций контекста, и подставлять его «как есть» в localItems нельзя.
      const fallback = sortedItemsRef.current;
      setLocalItems(fallback);
      localItemsRef.current = fallback;
    };

    if (!over) {
      resetLocal();
      return;
    }
    const overIdStr = String(over.id);

    if (activeTypeVal === "column") {
      const oldIndex = columnIds.indexOf(activeIdStr);
      const newIndex = columnIds.indexOf(overIdStr);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      const next = [...columnIds];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved!);
      reorderKanbanColumns(projectId, next);
      return;
    }

    // card drop — читаем порядок из ref (он точно отражает последний DragOver,
    // даже если React ещё не успел закоммитить state).
    const finalItems = localItemsRef.current;
    const movedItem = finalItems.find((i) => i.id === activeIdStr);
    if (!movedItem) {
      resetLocal();
      return;
    }
    const toCol = movedItem.status;

    // Если pointer оторвался от любого валидного контейнера — откатываемся.
    const overContainer = findContainerOf(overIdStr);
    if (!overContainer) {
      resetLocal();
      return;
    }

    const targetCol = finalItems.filter((i) => i.status === toCol);
    const idxInTarget = targetCol.findIndex((i) => i.id === activeIdStr);
    if (idxInTarget < 0) {
      resetLocal();
      return;
    }

    const original = itemsRef.current.find((i) => i.id === activeIdStr);
    const fromCol = original?.status ?? toCol;
    const origIdx = itemsRef.current
      .filter((i) => i.status === fromCol)
      .findIndex((i) => i.id === activeIdStr);

    if (fromCol === toCol && origIdx === idxInTarget) {
      // Действительно ничего не изменилось — но preview мог быть «жирным»
      // из-за промежуточных DragOver, поэтому всё равно синхронизируем.
      resetLocal();
      return;
    }

    moveBacklogItemToColumn(projectId, activeIdStr, toCol, idxInTarget);
    // localItems остаётся в новом порядке — когда придёт обновлённый
    // `items` prop, useEffect синхронизируется (activeIdRef уже null).
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveType(null);
    activeIdRef.current = null;
    const fallback = sortedItemsRef.current;
    setLocalItems(fallback);
    localItemsRef.current = fallback;
  };

  const activeCard =
    activeType === "card" && activeId
      ? localItems.find((i) => i.id === activeId) ?? null
      : null;
  const activeColumn =
    activeType === "column" && activeId
      ? sortedColumns.find((c) => c.id === activeId) ?? null
      : null;

  /* ─── Column CRUD ─── */
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColLabel, setNewColLabel] = useState("");

  const commitAddColumn = () => {
    const name = newColLabel.trim();
    if (!name) {
      setAddingColumn(false);
      setNewColLabel("");
      return;
    }
    const created = addKanbanColumn(projectId, name);
    if (created) toast.success(`Колонка «${created.label}» добавлена`);
    setAddingColumn(false);
    setNewColLabel("");
  };

  const [deleteColumnTarget, setDeleteColumnTarget] = useState<KanbanColumn | null>(null);
  const [deleteFallbackId, setDeleteFallbackId] = useState<string>("");

  const openDeleteDialog = (col: KanbanColumn) => {
    if (sortedColumns.length <= 1) {
      toast.error("Нельзя удалить последнюю колонку");
      return;
    }
    const count = itemsByColumn.get(col.id)?.length ?? 0;
    if (count === 0) {
      // Пусто — удаляем сразу.
      const res = removeKanbanColumn(projectId, col.id);
      if (!res.ok) toast.error("Не удалось удалить колонку");
      else toast.success("Колонка удалена");
      return;
    }
    const firstOther = sortedColumns.find((c) => c.id !== col.id);
    setDeleteFallbackId(firstOther?.id ?? "");
    setDeleteColumnTarget(col);
  };

  const confirmDeleteColumn = () => {
    if (!deleteColumnTarget) return;
    const res = removeKanbanColumn(
      projectId,
      deleteColumnTarget.id,
      deleteFallbackId || undefined,
    );
    if (!res.ok) {
      if (res.reason === "last_column") toast.error("Нельзя удалить последнюю колонку");
      else if (res.reason === "no_fallback") toast.error("Выберите колонку для переноса карточек");
      else toast.error("Не удалось удалить колонку");
      return;
    }
    toast.success(`Колонка «${deleteColumnTarget.label}» удалена, карточки перенесены`);
    setDeleteColumnTarget(null);
    setDeleteFallbackId("");
  };

  const moveColumn = (colId: string, delta: -1 | 1) => {
    const idx = columnIds.indexOf(colId);
    const newIdx = idx + delta;
    if (idx < 0 || newIdx < 0 || newIdx >= columnIds.length) return;
    const next = [...columnIds];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved!);
    reorderKanbanColumns(projectId, next);
  };

  return (
    <div className="relative">
      <DndContext
        sensors={sensors}
        collisionDetection={(args) => {
          // pointerWithin + rectIntersection работают лучше для cross-container dnd
          const pw = pointerWithin(args);
          if (pw.length > 0) return pw;
          const ri = rectIntersection(args);
          if (ri.length > 0) return ri;
          return closestCorners(args);
        }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="overflow-x-auto overscroll-x-contain pb-3">
          <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
            <div className="flex items-start gap-3">
              {sortedColumns.map((col, idx) => (
                <DraggableColumn
                  key={col.id}
                  column={col}
                  projectId={projectId}
                  items={itemsByColumn.get(col.id) ?? []}
                  canEdit={canEdit}
                  index={idx}
                  columnCount={sortedColumns.length}
                  expandedCardId={expandedCardId}
                  onToggleExpand={(id) =>
                    setExpandedCardId((prev) => (prev === id ? null : id))
                  }
                  onRename={(label) => renameKanbanColumn(projectId, col.id, label)}
                  onDeleteRequest={() => openDeleteDialog(col)}
                  onMoveLeft={() => moveColumn(col.id, -1)}
                  onMoveRight={() => moveColumn(col.id, 1)}
                  onAddCard={() =>
                    addBacklogItem(projectId, {
                      title: "Новая задача",
                      description: "",
                      priority: "medium",
                      status: col.id,
                      assignee: "",
                      storyPoints: 0,
                      sprint: "",
                      startDate: null,
                      dueDate: null,
                    })
                  }
                  onUpdateCard={(itemId, patch) => updateBacklogItem(projectId, itemId, patch)}
                  onDeleteCard={(itemId) => {
                    if (expandedCardId === itemId) setExpandedCardId(null);
                    removeBacklogItem(projectId, itemId);
                  }}
                />
              ))}

              {canEdit ? (
                <div className="flex-none" style={{ width: COLUMN_WIDTH_PX }}>
                  {addingColumn ? (
                    <div className="rounded-lg border border-border bg-background p-2 shadow-sm">
                      <Label
                        htmlFor="new-kanban-col"
                        className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground"
                      >
                        Название колонки
                      </Label>
                      <Input
                        id="new-kanban-col"
                        autoFocus
                        value={newColLabel}
                        onChange={(e) => setNewColLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitAddColumn();
                          if (e.key === "Escape") {
                            setAddingColumn(false);
                            setNewColLabel("");
                          }
                        }}
                        placeholder="Например, «Дизайн»"
                        className="h-8 text-sm"
                      />
                      <div className="mt-2 flex items-center justify-end gap-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            setAddingColumn(false);
                            setNewColLabel("");
                          }}
                        >
                          Отмена
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={commitAddColumn}
                        >
                          Добавить
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAddingColumn(true)}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/70 bg-muted/10 px-3 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/30 hover:text-[var(--nn-blue)]"
                    >
                      <Plus size={13} /> Добавить колонку
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </SortableContext>
        </div>

        <DragOverlay dropAnimation={{ duration: 220, easing: "cubic-bezier(0.22,1,0.36,1)" }}>
          {activeCard ? <CardVisual item={activeCard} dragging /> : null}
          {activeColumn ? (
            <div
              className="rounded-lg border border-border bg-background/95 px-3 py-2 text-sm font-semibold shadow-xl"
              style={{ width: COLUMN_WIDTH_PX }}
            >
              {activeColumn.label}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Диалог удаления колонки с выбором fallback */}
      <Dialog
        open={deleteColumnTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteColumnTarget(null);
            setDeleteFallbackId("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Удалить колонку</DialogTitle>
          </DialogHeader>
          {deleteColumnTarget ? (
            <div className="space-y-3 text-sm">
              <p>
                В колонке «<strong>{deleteColumnTarget.label}</strong>» есть карточки (
                {itemsByColumn.get(deleteColumnTarget.id)?.length ?? 0}). Куда перенести?
              </p>
              <div className="space-y-1">
                <Label htmlFor="kanban-fallback" className="text-xs text-muted-foreground">
                  Целевая колонка
                </Label>
                <select
                  id="kanban-fallback"
                  value={deleteFallbackId}
                  onChange={(e) => setDeleteFallbackId(e.target.value)}
                  className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[var(--nn-blue)]"
                >
                  {sortedColumns
                    .filter((c) => c.id !== deleteColumnTarget.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteColumnTarget(null);
                setDeleteFallbackId("");
              }}
            >
              Отмена
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDeleteColumn}>
              Удалить и перенести
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог детального просмотра карточки */}
      <CardDetailDialog
        item={localItems.find((it) => it.id === expandedCardId) ?? null}
        columns={sortedColumns}
        projectId={projectId}
        canEdit={canEdit}
        allProjects={projects}
        onClose={() => setExpandedCardId(null)}
        onChange={(patch) => {
          if (!expandedCardId) return;
          updateBacklogItem(projectId, expandedCardId, patch);
        }}
        onDelete={() => {
          if (!expandedCardId) return;
          const id = expandedCardId;
          setExpandedCardId(null);
          removeBacklogItem(projectId, id);
        }}
        onMoveToColumn={(colId) => {
          if (!expandedCardId) return;
          const targetCount = (itemsByColumn.get(colId) ?? []).length;
          moveBacklogItemToColumn(projectId, expandedCardId, colId, targetCount);
        }}
        onMoveToProject={async (toProjectId, toColumnId) => {
          if (!expandedCardId) return;
          const id = expandedCardId;
          setExpandedCardId(null);
          try {
            await moveBacklogItemToProject(projectId, toProjectId, id, toColumnId);
            const target = projects.find((p) => p.id === toProjectId);
            toast.success(
              `Карточка перенесена${target ? ` в «${target.shortTitle || target.title}»` : ''}`,
            );
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'Ошибка переноса';
            toast.error(`Не удалось перенести карточку: ${msg}`);
          }
        }}
      />
    </div>
  );
}

/* ─────────────────────────── DraggableColumn ────────────────────────── */

interface DraggableColumnProps {
  column: KanbanColumn;
  projectId: string;
  items: BacklogItem[];
  canEdit: boolean;
  index: number;
  columnCount: number;
  expandedCardId: string | null;
  onToggleExpand: (id: string) => void;
  onRename: (label: string) => void;
  onDeleteRequest: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onAddCard: () => void;
  onUpdateCard: (itemId: string, patch: Partial<BacklogItem>) => void;
  onDeleteCard: (itemId: string) => void;
}

function DraggableColumn({
  column,
  projectId,
  items,
  canEdit,
  index,
  columnCount,
  expandedCardId,
  onToggleExpand,
  onRename,
  onDeleteRequest,
  onMoveLeft,
  onMoveRight,
  onAddCard,
  onUpdateCard,
  onDeleteCard,
}: DraggableColumnProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: { type: "column" },
    disabled: !canEdit,
  });

  // Уникальный id для внутренней droppable-зоны карточек, чтобы НЕ
  // конфликтовать с droppable из useSortable выше (тот регистрирует drop-зону
  // на весь wrapper колонки по id `column.id`). Если бы id совпадали, второй
  // useDroppable перезаписал бы первый, ужав drop-rect только до области
  // карточек — и drop по заголовку колонки не срабатывал бы.
  const cardsDroppableId = `${column.id}::cards`;
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id: cardsDroppableId,
    data: { type: "column-cards", columnId: column.id },
  });

  const cardIds = useMemo(() => items.map((i) => i.id), [items]);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(column.label);

  useEffect(() => {
    if (!renaming) setDraft(column.label);
  }, [column.label, renaming]);

  const commitRename = () => {
    const name = draft.trim();
    if (name && name !== column.label) onRename(name);
    setRenaming(false);
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, width: COLUMN_WIDTH_PX }}
      className={cn(
        "flex-none",
        isDragging && "relative z-[25] opacity-90",
      )}
      aria-roledescription="column"
    >
      <div
        className={cn(
          "flex flex-col rounded-lg border border-border bg-muted/25",
          isOver && "ring-2 ring-[var(--nn-blue)]/60",
        )}
      >
        {/* Column header */}
        <div className="flex items-center gap-1 px-2 pb-1.5 pt-2">
          {canEdit ? (
            <button
              type="button"
              className="flex h-6 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded p-0 text-muted-foreground hover:bg-muted active:cursor-grabbing"
              aria-label={`Перетащить колонку «${column.label}»`}
              title="Перетащить колонку"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          ) : null}
          {renaming ? (
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") {
                  setDraft(column.label);
                  setRenaming(false);
                }
              }}
              className="h-7 flex-1 text-sm font-semibold"
            />
          ) : (
            <button
              type="button"
              className={cn(
                "min-w-0 flex-1 truncate text-left text-sm font-semibold text-foreground",
                canEdit && "hover:underline",
              )}
              onClick={() => canEdit && setRenaming(true)}
              disabled={!canEdit}
              title={canEdit ? "Переименовать" : column.label}
            >
              {column.label}
            </button>
          )}
          <span className="ml-1 shrink-0 rounded-full bg-background px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
            {items.length}
          </span>
          {canEdit ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded p-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={`Меню колонки «${column.label}»`}
                  title="Меню колонки"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setRenaming(true)}>
                  Переименовать…
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onMoveLeft} disabled={index === 0}>
                  Переместить влево
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={onMoveRight}
                  disabled={index === columnCount - 1}
                >
                  Переместить вправо
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={onDeleteRequest}
                  disabled={columnCount <= 1}
                >
                  Удалить колонку…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        {/* Cards */}
        <div
          ref={setDroppableRef}
          className="flex min-h-[120px] flex-col gap-1.5 px-2 pb-2"
        >
          <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
            {items.length === 0 ? (
              <div
                className={cn(
                  "flex min-h-[96px] items-center justify-center rounded-md border border-dashed border-border/70 bg-background/40 px-3 py-6 text-center text-[11px] italic text-muted-foreground transition-colors",
                  isOver && "border-[var(--nn-blue)]/60 bg-[var(--nn-blue-pale)]/30 text-[var(--nn-blue)]",
                )}
              >
                Перетащите карточку сюда
              </div>
            ) : (
              items.map((it) => (
                <SortableCard
                  key={it.id}
                  item={it}
                  projectId={projectId}
                  columnLabel={column.label}
                  expanded={expandedCardId === it.id}
                  canEdit={canEdit}
                  onToggleExpand={() => onToggleExpand(it.id)}
                  onUpdate={(patch) => onUpdateCard(it.id, patch)}
                  onDelete={() => onDeleteCard(it.id)}
                />
              ))
            )}
          </SortableContext>

          {canEdit ? (
            <button
              type="button"
              onClick={onAddCard}
              className="mt-1 inline-flex items-center justify-center gap-1 rounded-md border border-dashed border-border/60 bg-background/30 px-2 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/25 hover:text-[var(--nn-blue)]"
            >
              <Plus size={12} /> Добавить карточку
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── SortableCard ───────────────────────────── */

interface SortableCardProps {
  item: BacklogItem;
  projectId: string;
  columnLabel?: string;
  expanded: boolean;
  canEdit: boolean;
  onToggleExpand: () => void;
  onUpdate: (patch: Partial<BacklogItem>) => void;
  onDelete: () => void;
}

function SortableCard({
  item,
  projectId,
  columnLabel,
  expanded,
  canEdit,
  onToggleExpand,
  onUpdate,
  onDelete,
}: SortableCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: { type: "card", columnId: item.status },
    disabled: !canEdit || expanded,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} aria-roledescription="card" data-kanban-card-id={item.id}>
      <CardVisual
        item={item}
        projectId={projectId}
        columnLabel={columnLabel}
        expanded={expanded}
        canEdit={canEdit}
        onToggleExpand={onToggleExpand}
        onUpdate={onUpdate}
        onDelete={onDelete}
        dragHandleProps={canEdit ? { ...attributes, ...listeners } : undefined}
      />
    </div>
  );
}

/* ─────────────────────────── CardVisual ─────────────────────────────── */

interface CardVisualProps {
  item: BacklogItem;
  projectId?: string;
  columnLabel?: string;
  expanded?: boolean;
  canEdit?: boolean;
  dragging?: boolean;
  onToggleExpand?: () => void;
  onUpdate?: (patch: Partial<BacklogItem>) => void;
  onDelete?: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement> &
    Record<string, unknown>;
}

function CardVisual({
  item,
  columnLabel,
  dragging = false,
  onToggleExpand,
  dragHandleProps,
}: CardVisualProps) {
  const meta = PRIORITY_META[item.priority] ?? PRIORITY_META.medium;
  const rawAssignee = (item.assignee ?? "").trim();
  const { assigneeMatches } = useNotifications();
  const { canSeeDocuments } = useProjects();
  const match = rawAssignee ? assigneeMatches.get(normalizeAssigneeKey(rawAssignee)) ?? null : null;
  const assigneeLabel = rawAssignee ? preferredAssigneeLabel(rawAssignee, match) : "";
  const initials = initialsFor(match?.displayName || match?.login || rawAssignee);
  const isDone = !!columnLabel && isDoneColumnId(columnLabel);
  const due = describeDue(item.dueDate, { doneColumn: isDone });
  const dueTone = due ? DUE_TONE_STYLES[due.tone] : null;
  const tags = Array.isArray(item.tags) ? item.tags : [];
  // Просмотрщику вложения не показываем — пустой список прячет бейдж-скрепку на карточке.
  const attachments = canSeeDocuments && Array.isArray(item.attachments) ? item.attachments : [];
  const comments = Array.isArray(item.comments) ? item.comments : [];
  const subtasks = Array.isArray(item.subtasks) ? item.subtasks : [];
  const subtasksDone = subtasks.filter((s) => s.done).length;
  const subtasksAllDone = subtasks.length > 0 && subtasksDone === subtasks.length;

  return (
    <div
      className={cn(
        "group/card rounded-md border bg-background shadow-sm transition-shadow",
        dragging
          ? "border-[var(--nn-blue)] shadow-xl ring-2 ring-[var(--nn-blue)]/40 -rotate-[1deg]"
          : "border-border hover:shadow-md",
      )}
    >
      <div className="flex items-start gap-1.5 px-2 py-1.5">
        {dragHandleProps ? (
          <button
            type="button"
            className="mt-0.5 flex h-5 w-4 shrink-0 cursor-grab touch-none items-center justify-center rounded p-0 text-muted-foreground/50 opacity-0 transition-opacity hover:text-foreground group-hover/card:opacity-100 active:cursor-grabbing"
            aria-label={`Перетащить карточку «${item.title}»`}
            title="Перетащить"
            {...dragHandleProps}
          >
            <GripVertical className="h-3 w-3" strokeWidth={2} />
          </button>
        ) : (
          <span className="mt-0.5 h-5 w-4 shrink-0" aria-hidden />
        )}

        <span
          className="mt-1 block h-2 w-2 shrink-0 rounded-full"
          style={{ background: meta.color }}
          title={`Приоритет: ${meta.label}`}
          aria-hidden
        />

        <button
          type="button"
          onClick={() => onToggleExpand?.()}
          className="min-w-0 flex-1 text-left"
          title="Открыть карточку"
        >
          <span className="block line-clamp-2 text-[13px] font-medium leading-tight text-foreground">
            {item.title}
          </span>
        </button>
      </div>

      {(item.sprint || rawAssignee || due || tags.length > 0 || attachments.length > 0 || comments.length > 0 || subtasks.length > 0) ? (
        <div className="flex flex-wrap items-center gap-1.5 px-2 pb-1.5 text-[10px] text-muted-foreground">
          {tags.map((t) => {
            const meta = BACKLOG_TAGS[t];
            if (!meta) return null;
            return (
              <span
                key={t}
                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                style={{ background: meta.tint, color: meta.color }}
                title={`Тег: ${meta.label}`}
              >
                {meta.label}
              </span>
            );
          })}
          {item.sprint ? (
            <span className="inline-flex max-w-[8rem] items-center rounded-full bg-muted/60 px-1.5 py-0.5 font-medium uppercase tracking-wide">
              <span className="truncate">{item.sprint}</span>
            </span>
          ) : null}
          {attachments.length > 0 ? (
            <span
              className="inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[9px] font-semibold text-muted-foreground"
              title={`Вложений: ${attachments.length}`}
            >
              <Paperclip className="h-2.5 w-2.5" strokeWidth={2.5} />
              {attachments.length}
            </span>
          ) : null}
          {comments.length > 0 ? (
            <span
              className="inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[9px] font-semibold text-muted-foreground"
              title={`Комментариев: ${comments.length}`}
            >
              <MessageSquare className="h-2.5 w-2.5" strokeWidth={2.5} />
              {comments.length}
            </span>
          ) : null}
          {subtasks.length > 0 ? (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold tabular-nums",
                subtasksAllDone
                  ? "border-emerald-300/80 bg-emerald-50 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "border-border/60 bg-muted/40 text-muted-foreground",
              )}
              title={`Подзадачи: ${subtasksDone} из ${subtasks.length}${subtasksAllDone ? " · все выполнены" : ""}`}
            >
              <ListChecks className="h-2.5 w-2.5" strokeWidth={2.5} />
              {subtasksDone}/{subtasks.length}
            </span>
          ) : null}
          {due && dueTone ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold"
              style={{
                backgroundColor: dueTone.bg,
                color: dueTone.color,
                borderColor: dueTone.border,
              }}
              title={due.label}
            >
              <CalendarDays className="h-2.5 w-2.5" strokeWidth={2.5} />
              <span className="truncate">{due.short}</span>
            </span>
          ) : null}
          <span className="ml-auto inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
            style={{ background: meta.tint, color: meta.color }}>
            {meta.label}
          </span>
          {rawAssignee ? (
            <span
              className={cn(
                "inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold",
                match
                  ? "text-white"
                  : "border border-dashed border-border bg-muted/40 text-muted-foreground",
              )}
              style={match ? { backgroundColor: colorForAssignee(normalizeAssigneeKey(match.login)) } : undefined}
              aria-label={`Исполнитель: ${assigneeLabel}`}
              title={
                assigneeLabel +
                (match
                  ? match.isRegistered
                    ? ` — ${match.login}, зарегистрирован`
                    : ` — ${match.login}, приглашён`
                  : " — не найден среди пользователей")
              }
            >
              {initials || "?"}
            </span>
          ) : null}
        </div>
      ) : null}

    </div>
  );
}

/* ─────────────────────────── DateField ──────────────────────────────── */

interface DateFieldProps {
  label: string;
  value: string | null;
  onSave: (v: string | null) => void;
  readOnly?: boolean;
  minDate?: string;
  maxDate?: string;
  emphasizeDue?: boolean;
  done?: boolean;
}

/**
 * Компактный пикер даты: нативный `<input type="date">`, обёрнутый в оформление
 * под карточку. Показывает локализованный человеко-читаемый лейбл и цвет
 * под состояние (для дедлайна — просрочено/скоро/ок/завершено).
 */
function DateField({
  label,
  value,
  onSave,
  readOnly = false,
  minDate,
  maxDate,
  emphasizeDue = false,
  done = false,
}: DateFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const iso = toIsoDay(value);
  const due = emphasizeDue && iso ? describeDue(iso, { doneColumn: done }) : null;
  const tone = due ? DUE_TONE_STYLES[due.tone] : null;
  const displayText = iso ? formatShortRuDate(iso) : "—";
  const helperText = due ? due.label : "";
  const openPicker = () => {
    if (readOnly) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const anyEl = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof anyEl.showPicker === "function") {
      try {
        anyEl.showPicker();
      } catch {
        // Некоторые браузеры бросают InvalidStateError без жеста — игнорируем.
      }
    } else {
      el.click();
    }
  };

  return (
    <div className="min-w-0">
      <span className="mb-1 block text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div
        className={cn(
          "relative flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
          readOnly
            ? "cursor-default bg-muted/30"
            : "cursor-pointer bg-background hover:border-[var(--nn-blue)]/60",
        )}
        style={
          tone
            ? {
                backgroundColor: tone.bg,
                borderColor: tone.border,
                color: tone.color,
              }
            : undefined
        }
        onClick={openPicker}
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? -1 : 0}
        onKeyDown={(e) => {
          if (readOnly) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
      >
        <CalendarDays className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2.25} />
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            !iso && "text-muted-foreground",
            iso && !tone && "text-foreground",
          )}
        >
          {displayText}
        </span>
        {iso && !readOnly ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSave(null);
            }}
            className="inline-flex h-4 w-4 items-center justify-center rounded opacity-60 transition-opacity hover:bg-black/5 hover:opacity-100"
            aria-label={`Очистить ${label.toLowerCase()}`}
            title="Очистить дату"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
        <input
          ref={inputRef}
          type="date"
          value={iso ?? ""}
          min={minDate ?? undefined}
          max={maxDate ?? undefined}
          disabled={readOnly}
          onChange={(e) => {
            const v = e.target.value;
            onSave(v ? v : null);
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label={label}
          tabIndex={-1}
        />
      </div>
      {helperText ? (
        <span className="mt-0.5 block text-[10px] leading-tight" style={tone ? { color: tone.color } : undefined}>
          {helperText}
        </span>
      ) : null}
    </div>
  );
}

/* ───────────────────────── CardDetailDialog ─────────────────────────── */
/*
  Развёрнутый просмотр карточки — модалка в стиле Linear/Jira с двухколоночным
  макетом: слева описание/вложения/комментарии (главное), справа sidebar с мета
  (статус-колонка, приоритет, исполнитель, даты, теги, спринт).
*/

interface CardDetailDialogProps {
  item: BacklogItem | null;
  columns: KanbanColumn[];
  projectId: string;
  canEdit: boolean;
  /** Все проекты воркспейса — для пикера «Перенести в другой проект». */
  allProjects: Array<{
    id: string;
    title: string;
    shortTitle: string;
    kanbanColumns: KanbanColumn[];
  }>;
  onClose: () => void;
  onChange: (patch: Partial<BacklogItem>) => void;
  onDelete: () => void;
  onMoveToColumn: (columnId: string) => void;
  onMoveToProject: (toProjectId: string, toColumnId?: string) => void | Promise<void>;
}

function SidebarField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CardDetailDialog({
  item,
  columns,
  projectId,
  canEdit,
  allProjects,
  onClose,
  onChange,
  onDelete,
  onMoveToColumn,
  onMoveToProject,
}: CardDetailDialogProps) {
  const [moveOpen, setMoveOpen] = useState(false);
  const { projects: allProjectsFull, canSeeDocuments } = useProjects();
  const projectGanttTasks = useMemo(() => {
    const p = allProjectsFull.find((x) => x.id === projectId);
    return Array.isArray(p?.ganttTasks) ? p.ganttTasks : [];
  }, [allProjectsFull, projectId]);
  const projectHasFactSheet = useMemo(
    () => Boolean(allProjectsFull.find((x) => x.id === projectId)?.factGanttUrl),
    [allProjectsFull, projectId],
  );
  // Задачи факт-Ганта (из Google-таблицы) — грузим по требованию.
  const [factTasks, setFactTasks] = useState<Array<{ id: string; title: string }> | null>(null);
  const [factLoading, setFactLoading] = useState(false);
  const [factError, setFactError] = useState<string | null>(null);
  const loadFactTasks = useCallback(async () => {
    if (factLoading) return;
    setFactLoading(true);
    setFactError(null);
    try {
      const res = await apiFetchFactTasks(projectId);
      setFactTasks(res.tasks);
    } catch (e) {
      setFactError(e instanceof Error ? e.message : "Не удалось загрузить задачи факта");
      setFactTasks([]);
    } finally {
      setFactLoading(false);
    }
  }, [projectId, factLoading]);
  // Если открыли карточку, уже привязанную к факту, — подгрузим список задач факта.
  const itemSource = item?.ganttTaskSource;
  useEffect(() => {
    if (itemSource === "fact" && factTasks === null && projectHasFactSheet) void loadFactTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSource, projectId]);
  const open = !!item;
  if (!item) {
    return (
      <Dialog open={false} onOpenChange={() => onClose()}>
        <DialogContent />
      </Dialog>
    );
  }

  const meta = PRIORITY_META[item.priority] ?? PRIORITY_META.medium;
  const tags = Array.isArray(item.tags) ? item.tags : [];
  const attachments = Array.isArray(item.attachments) ? item.attachments : [];
  const comments = Array.isArray(item.comments) ? item.comments : [];
  const subtasks = Array.isArray(item.subtasks) ? item.subtasks : [];
  const currentCol = columns.find((c) => c.id === item.status);
  const isDone = !!currentCol && isDoneColumnId(currentCol.label);

  const toggleTag = (t: BacklogItemTag) => {
    const has = tags.includes(t);
    const next: BacklogItemTag[] = has ? tags.filter((x) => x !== t) : [...tags, t];
    onChange({ tags: next });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[92vh] w-[min(96vw,960px)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-border/70 bg-card px-5 py-3.5 pr-14">
          <span
            className="mt-2 block h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: meta.color }}
            title={`Приоритет: ${meta.label}`}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <DialogTitle asChild>
              <div className="min-w-0">
                <InlineText
                  value={item.title}
                  onSave={(v) => onChange({ title: v })}
                  className="text-[18px] font-semibold leading-tight text-foreground"
                  inputClassName="text-[18px] font-semibold w-full"
                  displayAs="span"
                  readOnly={!canEdit}
                  placeholder="Название задачи"
                />
              </div>
            </DialogTitle>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              В колонке «<span className="font-medium text-foreground/80">{currentCol?.label ?? item.status}</span>» ·
              обновлено {new Date(item.updatedAt).toLocaleString("ru-RU", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>

        {/* Body: 2-column */}
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[minmax(0,1fr)_280px]">
          {/* LEFT — main */}
          <div className="min-w-0 space-y-5 overflow-y-auto px-5 py-4">
            <section>
              <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Описание
              </h3>
              <RichTextEditor
                value={item.description ?? ""}
                onChange={(html) => onChange({ description: html })}
                placeholder="Опишите задачу, критерии приёмки, контекст…"
                minHeight="8rem"
                readOnly={!canEdit}
              />
            </section>

            <section>
              <BacklogSubtasks
                items={subtasks}
                onChange={(next: BacklogSubtask[]) => onChange({ subtasks: next })}
                readOnly={!canEdit}
                projectId={projectId}
              />
            </section>

            {projectId && canSeeDocuments ? (
              <section>
                <h3 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Paperclip className="h-3 w-3" strokeWidth={2} />
                  Вложения
                  {attachments.length > 0 ? (
                    <span className="rounded bg-muted/60 px-1 text-[9px] font-semibold tabular-nums text-muted-foreground">
                      {attachments.length}
                    </span>
                  ) : null}
                </h3>
                <BacklogAttachments
                  projectId={projectId}
                  items={attachments}
                  onChange={(next) => onChange({ attachments: next })}
                  readOnly={!canEdit}
                />
              </section>
            ) : null}

            <section>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <MessageSquare className="h-3 w-3" strokeWidth={2} />
                Комментарии
                {comments.length > 0 ? (
                  <span className="rounded bg-muted/60 px-1 text-[9px] font-semibold tabular-nums text-muted-foreground">
                    {comments.length}
                  </span>
                ) : null}
              </h3>
              <BacklogComments
                items={comments}
                onChange={(next) => onChange({ comments: next })}
                readOnly={!canEdit}
                projectId={projectId}
                onAttach={(att) => {
                  // Привязываем загруженную картинку к карточке как обычное
                  // вложение, чтобы её утянуло вместе с карточкой при переносе
                  // в другой проект и не осталось файла-сироты на диске.
                  const next = [...attachments, att];
                  onChange({ attachments: next });
                }}
              />
            </section>
          </div>

          {/* RIGHT — sidebar */}
          <aside className="space-y-3.5 overflow-y-auto border-t border-border/70 bg-muted/10 px-4 py-4 md:border-l md:border-t-0">
            <SidebarField label="Колонка">
              <select
                value={item.status}
                onChange={(e) => onMoveToColumn(e.target.value)}
                disabled={!canEdit}
                className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-[12px] font-medium text-foreground outline-none focus:ring-2 focus:ring-[var(--nn-blue)]/40 disabled:opacity-60"
              >
                {columns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </SidebarField>

            <SidebarField label="Приоритет">
              <InlineSelect
                value={item.priority}
                options={PRIORITY_OPTIONS as unknown as { value: BacklogItemPriority; label: string }[]}
                onSave={(v) => onChange({ priority: v })}
                readOnly={!canEdit}
                renderValue={(v) => (
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{
                      background: PRIORITY_META[v].tint,
                      color: PRIORITY_META[v].color,
                    }}
                  >
                    {PRIORITY_META[v].label}
                  </span>
                )}
              />
            </SidebarField>

            <SidebarField label="Исполнитель">
              <SingleAssigneePicker
                value={item.assignee ?? ""}
                onChange={(v) => onChange({ assignee: v })}
                readOnly={!canEdit}
                size="sm"
                placeholder="ФИО/логин"
              />
            </SidebarField>

            <div className="grid grid-cols-2 gap-2.5">
              <DateField
                label="Старт"
                value={item.startDate ?? null}
                onSave={(v) => onChange({ startDate: v })}
                readOnly={!canEdit}
                maxDate={item.dueDate ?? undefined}
              />
              <DateField
                label="Дедлайн"
                value={item.dueDate ?? null}
                onSave={(v) => onChange({ dueDate: v })}
                readOnly={!canEdit}
                minDate={item.startDate ?? undefined}
                emphasizeDue
                done={isDone}
              />
            </div>

            <SidebarField label="Спринт">
              <InlineText
                value={item.sprint ?? ""}
                onSave={(v) => onChange({ sprint: v })}
                className="text-[12px]"
                placeholder="—"
                displayAs="span"
                readOnly={!canEdit}
              />
            </SidebarField>

            <SidebarField label="Теги">
              <div className="flex flex-wrap gap-1.5">
                {ALL_BACKLOG_TAGS.map((t) => {
                  const tagMeta = BACKLOG_TAGS[t];
                  const active = tags.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTag(t)}
                      disabled={!canEdit}
                      aria-pressed={active}
                      className={cn(
                        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide transition-colors",
                        canEdit ? "cursor-pointer" : "cursor-default opacity-70",
                      )}
                      style={
                        active
                          ? {
                              background: tagMeta.tint,
                              color: tagMeta.color,
                              borderColor: tagMeta.color,
                            }
                          : {
                              background: "transparent",
                              color: "var(--muted-foreground)",
                              borderColor: "var(--border)",
                            }
                      }
                      title={active ? `Снять тег «${tagMeta.label}»` : `Добавить тег «${tagMeta.label}»`}
                    >
                      {tagMeta.label}
                    </button>
                  );
                })}
              </div>
            </SidebarField>

            {/* Поля для двухнедельной управленческой отчётности. */}
            <SidebarField label="Сторона ответственности">
              <div className="flex gap-1.5">
                {([
                  { v: "office", label: "Проектный офис", color: "#0055A5" },
                  { v: "customer", label: "ДИАК ЗФ (заказчик)", color: "#00A651" },
                ] as const).map((opt) => {
                  const active = (item.side ?? "office") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => onChange({ side: opt.v })}
                      disabled={!canEdit}
                      className={cn(
                        "inline-flex flex-1 items-center justify-center rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                        canEdit ? "cursor-pointer" : "cursor-default opacity-70",
                      )}
                      style={
                        active
                          ? { backgroundColor: opt.color, color: "#fff", borderColor: opt.color }
                          : {
                              background: "transparent",
                              color: "var(--muted-foreground)",
                              borderColor: "var(--border)",
                            }
                      }
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </SidebarField>

            <SidebarField label="Связанная задача Ганта">
              {(() => {
                const source: "plan" | "fact" = item.ganttTaskSource === "fact" ? "fact" : "plan";
                const setSource = (next: "plan" | "fact") => {
                  if (next === source) return;
                  // Смена источника сбрасывает привязку (id из разных пространств).
                  onChange({ ganttTaskSource: next, ganttTaskId: undefined });
                  if (next === "fact" && factTasks === null && !factLoading) void loadFactTasks();
                };
                return (
                  <div className="space-y-1.5">
                    {/* Переключатель источника */}
                    <div className="flex gap-1">
                      {([
                        { v: "plan", label: "План" },
                        { v: "fact", label: "Факт (таблица)" },
                      ] as const).map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setSource(opt.v)}
                          disabled={!canEdit}
                          className={cn(
                            "flex-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                            source === opt.v
                              ? "border-[var(--nn-blue)] bg-[var(--nn-blue)] text-white"
                              : "border-border bg-background text-muted-foreground hover:border-[var(--nn-blue)]/50",
                            !canEdit && "cursor-default opacity-70",
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {source === "plan" ? (
                      <>
                        <select
                          value={item.ganttTaskId ?? ""}
                          onChange={(e) => onChange({ ganttTaskSource: "plan", ganttTaskId: e.target.value || undefined })}
                          disabled={!canEdit || projectGanttTasks.length === 0}
                          className="w-full rounded-md border border-border bg-background px-2 py-1 text-[12px]"
                        >
                          <option value="">— не привязано —</option>
                          {projectGanttTasks.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.title.length > 60 ? `${t.title.slice(0, 60)}…` : t.title}
                            </option>
                          ))}
                        </select>
                        {(() => {
                          if (!item.ganttTaskId || !item.dueDate) return null;
                          const gt = projectGanttTasks.find((t) => t.id === item.ganttTaskId);
                          if (!gt || !gt.end) return null;
                          if (item.dueDate > gt.end) {
                            return (
                              <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[10.5px] text-amber-900">
                                ⚠ Дедлайн карточки ({item.dueDate}) позже конца задачи плана ({gt.end}).
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </>
                    ) : (
                      <>
                        {!projectHasFactSheet ? (
                          <div className="rounded border border-amber-200 bg-amber-50/60 px-2 py-1 text-[10.5px] text-amber-900">
                            Не задана факт-таблица. Укажите ссылку в разделе «Факт — рабочая таблица» на странице проекта.
                          </div>
                        ) : factLoading ? (
                          <div className="flex items-center gap-1.5 px-1 py-1 text-[11px] text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Читаю задачи из таблицы…
                          </div>
                        ) : factError ? (
                          <div className="rounded border border-amber-200 bg-amber-50/60 px-2 py-1 text-[10.5px] text-amber-900">
                            {factError}{" "}
                            <button type="button" onClick={() => void loadFactTasks()} className="underline">повторить</button>
                          </div>
                        ) : (
                          <select
                            value={item.ganttTaskId ?? ""}
                            onChange={(e) => onChange({ ganttTaskSource: "fact", ganttTaskId: e.target.value || undefined })}
                            disabled={!canEdit}
                            className="w-full rounded-md border border-border bg-background px-2 py-1 text-[12px]"
                          >
                            <option value="">— не привязано —</option>
                            {/* Если привязанной задачи нет в текущей выгрузке — покажем её id, чтобы не «потерять». */}
                            {item.ganttTaskId && !(factTasks ?? []).some((t) => t.id === item.ganttTaskId) ? (
                              <option value={item.ganttTaskId}>{item.ganttTaskId} (нет в таблице)</option>
                            ) : null}
                            {(factTasks ?? []).map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.id} — {t.title.length > 50 ? `${t.title.slice(0, 50)}…` : t.title}
                              </option>
                            ))}
                          </select>
                        )}
                        <div className="text-[10px] text-muted-foreground/80">
                          Задачи берутся из 1-й колонки (id) рабочей Google-таблицы проекта.
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </SidebarField>

            <SidebarField label="Нужно от руководства">
              <textarea
                value={item.escalation ?? ""}
                onChange={(e) =>
                  onChange({ escalation: e.target.value.trim() ? e.target.value : undefined })
                }
                disabled={!canEdit}
                rows={2}
                placeholder="Какое решение/действие нужно от руководства, чтобы продвинуться…"
                className="w-full resize-y rounded-md border border-border bg-background px-2 py-1 text-[12px]"
              />
            </SidebarField>
          </aside>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border/70 bg-card px-5 py-3">
          {canEdit ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  const title = (item.title || "").trim() || "эту карточку";
                  if (
                    !window.confirm(
                      `Удалить карточку «${title}»?\n\nКомментарии, вложения и теги тоже будут удалены. Действие необратимо.`,
                    )
                  ) {
                    return;
                  }
                  onDelete();
                }}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium text-red-700 transition-colors hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40"
              >
                <Trash2 className="h-3.5 w-3.5" /> Удалить карточку
              </button>
              <button
                type="button"
                onClick={() => setMoveOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
              >
                <ArrowRightLeft className="h-3.5 w-3.5" /> Перенести в другой проект
              </button>
            </div>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
          >
            Закрыть
          </button>
        </div>
      </DialogContent>
      <MoveCardDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        currentProjectId={projectId}
        cardTitle={item.title}
        attachmentCount={attachments.length}
        commentCount={comments.length}
        allProjects={allProjects}
        onConfirm={async (toProjectId, toColumnId) => {
          setMoveOpen(false);
          await onMoveToProject(toProjectId, toColumnId);
        }}
      />
    </Dialog>
  );
}

/* ───────────────────────── MoveCardDialog ────────────────────────────── */
/*
  Маленький диалог: выбор целевого проекта (любого, кроме текущего) и
  опционально — конкретной колонки в нём. Если колонку не задавать —
  сервер положит карточку в первую колонку целевого проекта.
*/

interface MoveCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentProjectId: string;
  cardTitle: string;
  attachmentCount: number;
  commentCount: number;
  allProjects: Array<{
    id: string;
    title: string;
    shortTitle: string;
    kanbanColumns: KanbanColumn[];
  }>;
  onConfirm: (toProjectId: string, toColumnId?: string) => void | Promise<void>;
}

function MoveCardDialog({
  open,
  onOpenChange,
  currentProjectId,
  cardTitle,
  attachmentCount,
  commentCount,
  allProjects,
  onConfirm,
}: MoveCardDialogProps) {
  const candidates = useMemo(
    () =>
      allProjects
        .filter((p) => p.id !== currentProjectId)
        .map((p) => ({
          id: p.id,
          label: p.shortTitle || p.title || p.id,
          columns: [...(p.kanbanColumns ?? [])].sort((a, b) => a.order - b.order),
        })),
    [allProjects, currentProjectId],
  );

  const [targetId, setTargetId] = useState<string>("");
  const [targetColId, setTargetColId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // При открытии/смене проекта — выставляем дефолты, чтобы не висели id-сироты.
  useEffect(() => {
    if (!open) return;
    if (candidates.length === 0) {
      setTargetId("");
      setTargetColId("");
      return;
    }
    if (!candidates.some((c) => c.id === targetId)) {
      const first = candidates[0]!;
      setTargetId(first.id);
      setTargetColId(first.columns[0]?.id ?? "");
    }
  }, [open, candidates, targetId]);

  useEffect(() => {
    const tgt = candidates.find((c) => c.id === targetId);
    if (!tgt) return;
    if (!tgt.columns.some((c) => c.id === targetColId)) {
      setTargetColId(tgt.columns[0]?.id ?? "");
    }
  }, [targetId, candidates, targetColId]);

  const targetColumns = candidates.find((c) => c.id === targetId)?.columns ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Перенести карточку в другой проект</DialogTitle>
        </DialogHeader>

        {candidates.length === 0 ? (
          <div className="py-2 text-sm text-muted-foreground">
            Других проектов в воркспейсе нет.
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-[12px] text-muted-foreground">
              <div className="line-clamp-2 text-foreground">«{cardTitle}»</div>
              {attachmentCount > 0 || commentCount > 0 ? (
                <div className="mt-1 text-[11px]">
                  Переедут вместе с карточкой:
                  {attachmentCount > 0 ? ` ${attachmentCount} вложений` : ""}
                  {attachmentCount > 0 && commentCount > 0 ? "," : ""}
                  {commentCount > 0 ? ` ${commentCount} комментариев` : ""}.
                </div>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="move-card-project" className="text-[12px]">
                Целевой проект
              </Label>
              <select
                id="move-card-project"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--nn-blue)]/30"
              >
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="move-card-column" className="text-[12px]">
                Колонка в целевом проекте
              </Label>
              <select
                id="move-card-column"
                value={targetColId}
                onChange={(e) => setTargetColId(e.target.value)}
                disabled={targetColumns.length === 0}
                className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--nn-blue)]/30 disabled:opacity-60"
              >
                {targetColumns.length === 0 ? (
                  <option value="">— нет колонок —</option>
                ) : (
                  targetColumns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))
                )}
              </select>
              {targetColumns.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  В этом проекте нет колонок канбана.
                </p>
              ) : null}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Отмена
          </Button>
          <Button
            type="button"
            disabled={!targetId || targetColumns.length === 0 || submitting}
            onClick={async () => {
              if (!targetId) return;
              setSubmitting(true);
              try {
                await onConfirm(targetId, targetColId || undefined);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? "Переношу…" : "Перенести"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
